import { t } from "i18next";
import PaginationUI from "../atoms/PaginationUI";
import ColumnButton from "../molecules/ColumnButton";
import ColumnDropdownAvatar from "../molecules/ColumnDropdownAvatar";
import ColumnPersonInformationAvatar from "../molecules/ColumnPersonInformationAvatar";
import ColumnText from "../molecules/ColumnText";
import LoadTable from "./LoadTable";
import ColumnDate from "../molecules/ColumnDate";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";

export type userRecoveryProps = {
  name: string;
  image: string;
  email: string;
};

export type AssignedPageProps = {
  user: userRecoveryProps;
  assigned: string;
  lastVisit: string;
  actionRecommendation: string;
  frequencyMonth: string;
};

export type TableListAssignedProps = {
  people: AssignedPageProps[];
  loader: boolean;
  support: any;
  openIncidence: (tike: any) => void;
};

const TableListAssigned: React.FC<TableListAssignedProps> = ({
  people,
  loader,
  support,
  openIncidence,
}) => {
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const totalResults = people?.length || 0;
  const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);

  useEffect(() => {
    if (loader) return;

    if (totalResults === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      } else if (currentPage < 1 && totalPages > 0) {
        setCurrentPage(1);
      }
    }
  }, [totalResults, totalPages, currentPage, loader]);

  const paginatedPeople = useMemo(() => {
    if (!people || people.length === 0) {
      return [];
    }

    let pageToUse = currentPage;
    if (totalPages > 0) {
      if (currentPage > totalPages) pageToUse = totalPages;
      if (currentPage < 1) pageToUse = 1;
    } else {
      pageToUse = 1;
    }

    const startIndex = (pageToUse - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return people.slice(startIndex, endIndex);
  }, [people, currentPage, totalPages]);

  if (loader) {
    return <LoadTable />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-0">
      <div className="mt-2 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full  py-2 align-middle sm:px-6 lg:px-8">
            <div className="max-h-[65vh] overflow-y-auto ">
              <table className="min-w-full divide-y divide-gray-300 ">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4  text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8"
                    >
                      {t("assigned.views.user")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5  text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      {t("assigned.views.personal_center")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      {t("assigned.views.last_visit")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      {t("assigned.views.action_recomended")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      {t("assigned.views.month_frecuency")}
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      {t("assigned.views.incidencia")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedPeople?.map((person) => (
                    <tr key={person?.id}>
                      <ColumnPersonInformationAvatar
                        id={person?.id}
                        open_user={true}
                        name={person?.name}
                        image={person?.image}
                        email={person?.email}
                      />
                      <ColumnDropdownAvatar data={support} />
                      <ColumnDate date={person?.last_visited} />
                      <ColumnText title={person?.actionRecommendation} />
                      <ColumnText
                        title={`${person?.frequency_month ?? 0} veces`}
                      />
                      {people?.tickets?.length > 0 && (
                        <div onClick={() => openIncidence(person)}>
                          <ColumnButton name="Ver incidencia" />
                        </div>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <PaginationUI
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => {
            setCurrentPage(page);
          }}
          pageSize={ITEMS_PER_PAGE}
          totalResults={totalResults}
        />
      </div>
    </div>
  );
};

export default TableListAssigned;
